import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ModalWrapper, SectionTitle } from 'modules/shared/components';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from 'utils/logger';
import { PayrollRow, payAdvanceSalary } from '../../hr.api';
import { money, monthLabel } from '../payroll.utils';

const PAYMENT_METHODS = ['Bank Transfer', 'UPI', 'Cheque', 'Cash'];

/** `YYYY-MM-DD` for the last day of the payroll month, so an advance lands on the month it belongs to. */
const defaultDate = (year: number, month: number) => {
  const today = new Date();
  const endOfMonth = new Date(year, month, 0);
  const target = today < endOfMonth ? today : endOfMonth;
  return target.toISOString().slice(0, 10);
};

/**
 * Records an advance drawn against a coming salary.
 *
 * It is written as a debit of type `advance_salary`, which is exactly what the register has
 * always read its "advance" column from — nothing could create one until now, so the column
 * was always blank and the deduction never happened.
 */
export default function AdvanceSalaryModal({
  open,
  row,
  onClose,
  onSaved,
}: {
  open: boolean;
  row: PayrollRow | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS[0]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!row) return;
    setAmount('');
    setPaymentMethod(PAYMENT_METHODS[0]);
    setPaymentDate(defaultDate(row.year, row.month));
  }, [row]);

  const handleSave = async () => {
    if (!row) return;
    if (!(Number(amount) > 0)) {
      toast.error('Enter an advance amount greater than zero');
      return;
    }

    setSaving(true);
    try {
      await payAdvanceSalary({
        employee_id: row.employee_id,
        amount,
        payment_date: paymentDate,
        payment_method: paymentMethod,
        particulars: `Advance salary — ${monthLabel(row.year, row.month)}`,
      });
      toast.success('Advance recorded and deducted from this month');
      await onSaved();
      onClose();
    } catch (err) {
      toast.error(
        logError({ error: err, message: 'Failed to record the advance', code: 'ADVANCE_SALARY_FAILED' })
      );
    } finally {
      setSaving(false);
    }
  };

  if (!row) return null;

  return (
    <ModalWrapper open={open} onClose={onClose} maxWidth='xs'>
      <SectionTitle title='Advance salary' />
      <Typography variant='body2' color='text.secondary'>
        {row.employee_name} — {monthLabel(row.year, row.month)}. Current net payable{' '}
        {money(row.net_payable)}.
      </Typography>

      <Stack gap={2}>
        <TextField
          size='small'
          type='number'
          label='Amount (₹)'
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputProps={{ min: 0, step: 100 }}
          autoFocus
        />
        <TextField
          size='small'
          type='date'
          label='Paid on'
          value={paymentDate}
          onChange={(e) => setPaymentDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText='The month this date falls in is the month it is deducted from.'
        />
        <TextField
          select
          size='small'
          label='Payment method'
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
        >
          {PAYMENT_METHODS.map((method) => (
            <MenuItem key={method} value={method}>
              {method}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Stack direction='row' gap={2} justifyContent='flex-end'>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Record advance'}
        </Button>
      </Stack>
    </ModalWrapper>
  );
}
